import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { environment } from '../../environments/environment';
import { getDateWithTimezone, getFormattedShortDate, getformattedShortStartEndDate } from '../../utils/dates';
import { exec } from '../../utils/utils';
import { FirestoreOpenHouseEvent, FullAgentEventRequestEntry, OpenHouseEvent } from '../app.models';
import { AlgoliaHit } from '../constants/algolia';
import { OpenHouseEventStatus, CreateUpdateEventPayload } from '../types';
import { AuthService } from './auth.service';
import { CommonProvider } from './common';
import { NetworkProvider } from './network';
import { UtilsService } from './utils.service';

type CalendarServerResponse<T extends any> = Promise<{
  ok: boolean;
  data: T;
}>;

// Due to Firestore security rules, we returns some data from the backend.
type PartiallyReconstructedOpenHouseEvent = FirestoreOpenHouseEvent & {
  agentRequests?: FullAgentEventRequestEntry[];
  didAgentRequestEvent?: boolean;
  listingAddress: string;
  agentImageURL: string;
  agent: AlgoliaHit;
};

@Injectable({
  providedIn: 'root',
})
export class OpenHouseSchedingService {
  constructor(private http: HttpClient, private auth: AuthService, private network: NetworkProvider, private common: CommonProvider, private utilsService: UtilsService) {}

  async _makeRequest<Payload, Response = undefined>(url: string, payload?: Payload): CalendarServerResponse<Response> {
    if (!this.network.checkInternet()) {
      return {
        ok: false,
        data: undefined,
      };
    }

    const [response, _error] = await exec(this.http.post<CalendarServerResponse<Response>>(url, payload, { headers: this.auth.getHeaders() }).toPromise());

    if (_error) {
      const error = _error.error;
      const errorMsg = `Error calling scheduler endpoint ${url} ${payload ? `with payload ${JSON.stringify(payload)}` : ''}. Error ${error.message}`;

      console.error(errorMsg);

      const isSevereError = _error.status >= 500;
      this.auth.logErrors(errorMsg, isSevereError);

      this.common.closeLoading();

      return {
        ok: false,
        data: error.message,
      };
    }

    return response;
  }

  async getAllEventsFromListing(listingId: string): CalendarServerResponse<OpenHouseEvent[]> {
    // if (User_global.role !== 'admin') {
    //   throw new Error('Only admins can get all events');
    // }
    const user = await this.auth.getCurrentFullUser();
    if (!user?.role) {
      this.auth.logErrors(`User wasn't present on "getAllEventsFromListing" call`, false);
      return {
        ok: false,
        data: [],
      };
    }

    const eventsResults = await this._makeRequest<{ listingId: string }, PartiallyReconstructedOpenHouseEvent[]>(environment.getAllEventsFromListing, { listingId });

    if (!eventsResults.ok) {
      return {
        ok: false,
        data: undefined,
      };
    }

    const promises = eventsResults.data.map((x) => this.fromPartialToFullEvent(x, user.timezone));

    return {
      ok: true,
      data: await Promise.all(promises),
    };
  }

  async createEvent(newEvent: OpenHouseEvent): CalendarServerResponse<FirestoreOpenHouseEvent> {
    const { eventId, start, end, agentId, status, requiresApproval, internalNote, listingId } = newEvent;

    const event = await this._makeRequest<CreateUpdateEventPayload, FirestoreOpenHouseEvent>(environment.createOpenHouseEvent, {
      eventId,
      start: start.getTime(),
      end: end.getTime(),
      status,
      requiresApproval,
      internalNote,
      listingId,
      agentId,
    });

    return event;
  }

  async updateEvent(updatedEvent: CreateUpdateEventPayload, currentUserTimezone: string): CalendarServerResponse<OpenHouseEvent> {
    const event = await this._makeRequest<CreateUpdateEventPayload, PartiallyReconstructedOpenHouseEvent>(environment.updateOpenHouseEvent, updatedEvent);

    const fullEvent = await this.fromPartialToFullEvent(event.data, currentUserTimezone);

    return {
      ok: event.ok,
      data: fullEvent,
    };
  }

  async deleteOpenHouseEvent(eventId: string, listingId: string) {
    const event = await this._makeRequest<{ eventId: string; listingId: string }>(environment.deleteOpenHouseEvent, {
      eventId,
      listingId,
    });

    return event;
  }

  public usingCachedResults = false;
  async getAgentOpenHouseEvents(userId: string): Promise<OpenHouseEvent[]> {
    // TODO: Only allow users with role 'agent' to call this function

    const user = await this.auth.getCurrentFullUser();
    if (!user?.role) {
      this.auth.logErrors(`User wasn't present on "getAgentOpenHouseEvents" call`, false);
      return [];
    }

    const eventsResults = await this._makeRequest<{}, PartiallyReconstructedOpenHouseEvent[]>(environment.getAgentOpenHouseEvents, {});

    if (!eventsResults.ok) {
      const hasCachedResults = localStorage.getItem(`cachedOpenHouseEvents-${userId}`);

      if (hasCachedResults) {
        const cachedEvents = JSON.parse(hasCachedResults) as OpenHouseEvent[];
        this.usingCachedResults = true;

        return cachedEvents;
      } else {
        return [];
      }
    }

    const promises = eventsResults.data.map((x) => this.fromPartialToFullEvent(x, user.timezone));

    const results = await Promise.all(promises);

    this.usingCachedResults = false;

    // @TODO Invalidate cache after some time
    localStorage.setItem(`cachedOpenHouseEvents-${userId}`, JSON.stringify(results));

    return results;
  }

  async getAgentOpenHouseEventsForListing(listingId: string): Promise<OpenHouseEvent[]> {
    // TODO: Only allow users with role 'agent' to call this function
    const user = await this.auth.getCurrentFullUser();
    if (!user?.role) {
      this.auth.logErrors(`User wasn't present on "getAgentOpenHouseEventsForListing" call`, false);
      return [];
    }

    const eventsResults = await this._makeRequest<{ listingId: string }, PartiallyReconstructedOpenHouseEvent[]>(environment.getAgentOpenHouseEventsFromListing, { listingId });

    if (!eventsResults.ok) {
      return [];
    }

    const promises = eventsResults.data.map((x) => this.fromPartialToFullEvent(x, user.timezone));

    return Promise.all(promises);
  }

  async requestOpenHouseEvent(eventToRequest: { eventId: string; listingId: string }, currentUserTimezone: string): CalendarServerResponse<PartiallyReconstructedOpenHouseEvent> {
    const event = await this._makeRequest<{ eventId: string; listingId: string }, PartiallyReconstructedOpenHouseEvent>(environment.requestOpenHouseEvent, eventToRequest);

    return {
      ok: event.ok,
      data: event.data,
    };
  }

  /// Utils

  async fromPartialToFullEvent(event: PartiallyReconstructedOpenHouseEvent, timezone: string): Promise<OpenHouseEvent> {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);

    const start = getDateWithTimezone(startDate, timezone);
    const end = getDateWithTimezone(endDate, timezone);

    const formattedShortDate = getFormattedShortDate(start);
    const formattedShortStartEndDate = getformattedShortStartEndDate(start, end);

    const classNames = event.status === OpenHouseEventStatus.assigned ? 'assigned-event' : 'free-event';
    const color = this.utilsService.getOpenHouseEventColor(event.listingAddress, event.agent);

    let agentAvatarName = '';
    if (event.status === OpenHouseEventStatus.assigned) {
      event.agent.objectID = event.agentId;
      agentAvatarName = this.auth.createAvatarName(event.agent.username);
    }

    const now = moment();
    const isPending = now.isBefore(end);

    return {
      ...event,

      start: start.toDate(),
      end: end.toDate(),

      formattedShortDate,
      formattedShortStartEndDate,

      agentAvatarName,

      isPending,

      classNames,
      color,
    };
  }
}
